import { UseMutationOptions } from '@tanstack/react-query';

import Constants from '@/Constants';
import {
  AtHomeLoginResponse,
  GenericApiResponse,
  GetStudentByAccessTokenResponse,
  ResetAppResponse,
  SendOtpResponse,
  makeRequest,
} from '@/services/api';
import { createKey } from '@/utils/helper';

import { useApiMutation, useApiQuery } from '../useApi';

export const useVerifyAccessCode = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<AtHomeLoginResponse>,
      unknown,
      { accessCode: string; programCode: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.VERIFY_AT_HOME_ACCESS_CODE_URL),
    mutationFn: async ({
      accessCode,
      programCode,
    }: {
      accessCode: string;
      programCode: string;
    }) => {
      const response = await makeRequest<AtHomeLoginResponse>(
        Constants.VERIFY_AT_HOME_ACCESS_CODE_URL,
        {
          accessCode,
          programCode,
        },
      );
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useGetStudentProfile = () => {
  const response = localStorage.getItem('atHomeLoginResponse') || '';
  const { accessToken } = JSON.parse(response) as AtHomeLoginResponse;
  const { token } = accessToken;
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENT_BY_ACCESS_TOKEN_URL),
    queryFn: async () => {
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await makeRequest<GetStudentByAccessTokenResponse>(
        Constants.GET_STUDENT_BY_ACCESS_TOKEN_URL,
        {},
        headers,
        'get',
      );
      return result;
    },
    select: (data) => data.data,
    enabled: !!token,
  });
};

export const useSendOtp = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<SendOtpResponse>,
      unknown,
      { mobile: string; programCode: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.SEND_OTP_URL),
    mutationFn: async ({ mobile, programCode }: { mobile: string; programCode: string }) => {
      const response = await makeRequest<SendOtpResponse>(Constants.SEND_OTP_URL, {
        mobile,
        programCode,
      });
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useVerifyOtp = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<AtHomeLoginResponse>,
      unknown,
      { mobile: string; otp: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  return useApiMutation({
    mutationKey: createKey(Constants.VERIFY_OTP_URL),
    mutationFn: async ({ mobile, otp }: { mobile: string; otp: string }) => {
      const response = await makeRequest<AtHomeLoginResponse>(Constants.VERIFY_OTP_URL, {
        mobile,
        otp,
      });
      return response;
    },
    retry: false,
    ...options,
  });
};

export const useResetApp = (
  options?: Omit<
    UseMutationOptions<
      GenericApiResponse<ResetAppResponse>,
      unknown,
      { refreshToken: string },
      unknown
    >,
    'mutationFn'
  >,
) => {
  const response = localStorage.getItem('atHomeLoginResponse') || '';
  const { accessToken } = JSON.parse(response) as AtHomeLoginResponse;
  const { token } = accessToken;
  return useApiMutation({
    mutationKey: createKey(Constants.RESET_APP_URL),
    mutationFn: async ({ refreshToken }: { refreshToken: string }) => {
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await makeRequest<ResetAppResponse>(
        Constants.RESET_APP_URL,
        {
          refreshToken,
        },
        headers,
      );
      return result;
    },
    retry: false,
    ...options,
  });
};

export const useGetStudentByMobile = () => {
  const response = localStorage.getItem('atHomeLoginResponse') || '';
  const { accessToken } = JSON.parse(response) as AtHomeLoginResponse;
  const { token } = accessToken;
  return useApiQuery({
    queryKey: createKey(Constants.GET_STUDENT_BY_MOBILE),
    queryFn: async () => {
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const result = await makeRequest<GetStudentByAccessTokenResponse>(
        Constants.GET_STUDENT_BY_MOBILE,
        {},
        headers,
        'get',
      );
      return result;
    },
    select: (data) => data.data,
    enabled: !!token,
  });
};
